import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { EventListItem, EventUpdate, EventCreate, SeatSelection, EventCarriagesPayload, CarriageInput, CarriageTicketInput } from '../types'

export function useEvents() {
  return useQuery({
    queryKey: ['events'],
    queryFn: ApiClient.events
  })
}

// Single event detail. Cache key includes the id so navigating between
// events doesn't show stale data. Disabled if the id is invalid (e.g. the
// URL param failed to parse) - the page component handles that case.
export function useEvent(id: number) {
  return useQuery({
    queryKey: ['events', id],
    queryFn: () => ApiClient.event(id),
    enabled: Number.isFinite(id) && id > 0
  })
}

export function useEventTypes() {
  return useQuery({
    queryKey: ['event-types'],
    queryFn: ApiClient.eventTypes,
    staleTime: 60 * 60 * 1000 // catalogues rarely change - cache for an hour
  })
}

export function useDepartments() {
  return useQuery({
    queryKey: ['departments'],
    queryFn: ApiClient.departments,
    staleTime: 60 * 60 * 1000
  })
}

// Batch MERAC summary for the Events list cards (presence + ticket/income +
// last-refreshed, keyed by event_id). One request for the whole grid.
// staleTime keeps the "refreshed X ago" stamp reasonably live without
// hammering the endpoint on every navigation.
export function useMeracSummary() {
  return useQuery({
    queryKey: ['merac-summary'],
    queryFn: ApiClient.meracSummary,
    staleTime: 5 * 60 * 1000
  })
}

// Mutation: PUT /api/events/:id. On success we write the response straight
// into the per-event cache (no follow-up refetch needed) and invalidate the
// list query so the events grid reflects the new name/status/dates next time
// it's visible. The handler returns the same wire shape as GET, so the cache
// write is a drop-in.
export function useUpdateEvent(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EventUpdate) => ApiClient.updateEvent(id, body),
    onSuccess: (updated: EventListItem) => {
      qc.setQueryData<EventListItem>(['events', id], updated)
      // refetchQueries (vs. invalidate) forces the list to refresh even if
      // there's no current observer - dashboard "upcoming" + EventsList may
      // both reflect the edit on next mount.
      qc.refetchQueries({ queryKey: ['events'], exact: true })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    }
  })
}

// Mutation: POST /api/events. On success we seed the per-event cache so
// navigating to /events/:id of the new event renders immediately without a
// network round trip, and refetch the list so the new card appears even if
// the user lands on /events with a stale cache.
export function useCreateEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EventCreate) => ApiClient.createEvent(body),
    onSuccess: (created: EventListItem) => {
      qc.setQueryData<EventListItem>(['events', created.id], created)
      qc.refetchQueries({ queryKey: ['events'], exact: true })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    }
  })
}

// The Carriages card's data: the formation as carriage instances with their
// ticket assignments, plus the Seats choice.
export function useEventCarriages(id: number) {
  return useQuery({
    queryKey: ['events', id, 'carriages'],
    queryFn: () => ApiClient.eventCarriages(id)
  })
}

// Both card mutations return the full carriages payload: set it as the card's
// data and mirror the codes / Seats choice onto the cached event.
function applyCarriagesPayload(qc: ReturnType<typeof useQueryClient>, id: number, payload: EventCarriagesPayload) {
  qc.setQueryData<EventCarriagesPayload>(['events', id, 'carriages'], payload)
  qc.setQueryData<EventListItem>(['events', id], (prev) =>
    prev ? { ...prev, carriages: payload.carriages.map((c) => c.code), seatSelection: payload.seatSelection } : prev)
  qc.invalidateQueries({ queryKey: ['events'], exact: true })
}

export function useUpdateCarriageTickets(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ carriageId, tickets }: { carriageId: number; tickets: CarriageTicketInput[] }) =>
      ApiClient.updateCarriageTickets(id, carriageId, tickets),
    onSuccess: (payload) => applyCarriagesPayload(qc, id, payload)
  })
}

// The Assign dialog's list: the MERAC ticket classes selling on the event + the
// ticket build. staleTime 0 so every time the dialog mounts it refetches — the
// MERAC sync and the ticket build both change underneath it.
export function useCarriageTicketOptions(id: number, enabled = true) {
  return useQuery({
    queryKey: ['events', id, 'carriages', 'ticket-options'],
    queryFn: () => ApiClient.carriageTicketOptions(id),
    staleTime: 0,
    enabled: enabled && Number.isFinite(id) && id > 0
  })
}

export function useUpdateEventCarriages(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { carriages?: CarriageInput[]; seatSelection?: SeatSelection | null }) => ApiClient.updateEventCarriages(id, body),
    onSuccess: (payload) => applyCarriagesPayload(qc, id, payload)
  })
}

// Merge same-named duplicate events into one multi-date event (item 8).
export function useMergeEvents() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ primaryId, mergeIds }: { primaryId: number; mergeIds: number[] }) =>
      ApiClient.mergeEvents(primaryId, mergeIds),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['events', res.primaryId] })
      qc.refetchQueries({ queryKey: ['events'], exact: true })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    }
  })
}